import { getAuthHeaders } from './authUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

function apiUrl(path) {
  const cleanPath = String(path || '').replace(/^\/+/, '');
  const origin = API_ORIGIN || API_BASE_URL.replace(/\/api\/?$/, '') || '';
  return `${origin.replace(/\/+$/, '')}/api/${cleanPath.replace(/^api\/+/, '')}`;
}

class ConfigApiService {
  async getServices() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/services`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error getting services
      throw error;
    }
  }
  async getConfigByKey(configKey) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/${configKey}?t=${Date.now()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      // Error getting config
      throw error;
    }
  }

  async saveConfigByKey(configKey, payload) {
    try {
      const body = typeof payload === 'object' && payload && !payload.data ? { data: payload } : payload;
      const response = await fetch(`${API_BASE_URL}/api/config/${configKey}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      // Error saving config
      throw error;
    }
  }

  async transformByKey(configKey, { source, dto, preview = false } = {}) {
    try {
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const response = await fetch(`${apiBase}/api/transform/${configKey}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ source, dto, preview })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      // Error transforming
      throw error;
    }
  }

  async fetchSampleByKey(configKey, provider) {
    try {
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const url = provider ? `${apiBase}/api/sample/${configKey}?provider=${encodeURIComponent(provider)}` : `${apiBase}/api/sample/${configKey}`;
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      // Error fetching sample
      throw error;
    }
  }

  async fetchSampleByKeyWithOverrides(configKey, provider, overrides = {}) {
    try {
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const params = new URLSearchParams();
      if (provider) params.set('provider', provider);
      // Flatten overrides.query onto URL params to let backend merge/override
      if (overrides && typeof overrides === 'object') {
        const q = overrides.query || {};
        Object.entries(q).forEach(([k, v]) => { if (v != null) params.set(k, String(v)); });
      }
      const url = `${apiBase}/api/sample/${encodeURIComponent(configKey)}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error fetching sample (overrides)
      throw error;
    }
  }
  async promoteSampleFromWebhook(configKey) {
    try {
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const response = await fetch(`${apiBase}/api/sample/${encodeURIComponent(configKey)}/promote-from-webhook`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      // Error promoting sample
      throw error;
    }
  }
  async saveNetsuiteM2M(credentials) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/netsuite-m2m`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(credentials)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async saveAdobeOAuth2(credentials) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/adobe-oauth2`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(credentials)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error saving Adobe OAuth2 credentials
      throw error;
    }
  }

  async saveAdobeToken(credentials) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/adobe-token`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(credentials)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error saving Adobe token credentials
      throw error;
    }
  }

  async saveAdobeCredentials(credentials) {
    try {
      // Use the new Adobe endpoint that supports PaaS/SaaS format
      const response = await fetch(`${API_BASE_URL}/api/adobe/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(credentials)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Error saving Adobe Commerce credentials
      throw error;
    }
  }

  async getConfigStatus(configKey) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/status/${configKey}?t=${Date.now()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Error getting config status
      throw error;
    }
  }

  async getAllStatuses() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/status?t=${Date.now()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      // Error getting all config statuses
      throw error;
    }
  }

  async getCredentials(configKey) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/${configKey}?t=${Date.now()}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Error getting credentials
      throw error;
    }
  }

  async getNetSuiteAccounts(types = []) {
    try {
      const params = new URLSearchParams();
      const requestedTypes = Array.isArray(types) ? types.filter(Boolean) : [types].filter(Boolean);
      if (requestedTypes.length > 0) params.set('types', requestedTypes.join(','));
      params.set('t', String(Date.now()));
      const url = `${apiUrl('/netsuite/accounts')}?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async validateCredentials(configKey, payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/${encodeURIComponent(configKey)}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload || {})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, ...(err || {}), status: response.status };
      }
      return await response.json();
    } catch (error) {
      // Error validating credentials
      return { success: false, error: 'Validation request failed' };
    }
  }

  async saveBearerCredentials(configKey, payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/bearer/${encodeURIComponent(configKey)}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload || {})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async saveWayfairCredentials(payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/wayfair`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload || {})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async saveQuickBooksCredentials(payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/quickbooks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload || {})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async startQuickBooksOAuth(payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/credentials/quickbooks/oauth/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload || {})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
  
  async getShopifyConfig() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shopify/config`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error getting Shopify config
      throw error;
    }
  }
  
  async saveShopifyConfig(payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shopify/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error saving Shopify config
      throw error;
    }
  }
  
  async getSageConfig() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sage/config`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error getting SAGE config
      throw error;
    }
  }
  
  async saveSageConfig(payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sage/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error saving SAGE config
      throw error;
    }
  }
  
  async validateSageConnection() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sage/config/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error validating SAGE connection
      return { success: false, error: error.message };
    }
  }
  
  async validateShopifyConnection() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shopify/config/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      // Error validating Shopify connection
      return { success: false, error: error.message };
    }
  }
}

export const configApi = new ConfigApiService();
